// components/industry-templates/RetailEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ShoppingBag, Tag, TrendingUp, Gift } from 'lucide-react';

export const RetailEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Brand Showcase */}
      {industryData?.brand_showcase && (
        <Card className="border-0 bg-gradient-to-br from-pink-500/20 to-purple-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <ShoppingBag className="w-8 h-8" style={{ color: theme.primary }} />
              Featured Brands & Retailers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {industryData.brand_showcase.map((brand: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20 hover:scale-105 transition-transform text-center">
                  <div className="text-3xl mb-3">🛍️</div>
                  <h4 className="font-bold text-white mb-2">{brand.name}</h4>
                  {brand.discount && (
                    <Badge className="bg-yellow-500 text-black">
                      {brand.discount}% OFF
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exclusive Deals */}
      {industryData?.exclusive_deals && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(236, 72, 153, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Tag className="w-6 h-6" style={{ color: theme.primary }} />
              Exclusive Event Deals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.exclusive_deals.map((deal: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{deal.offer}</h3>
                      <p className="text-slate-300">{deal.brand}</p>
                    </div>
                    <Badge className="bg-red-500 text-white text-lg py-2 px-4">
                      {deal.discount}
                    </Badge>
                  </div>
                  {deal.limited_time && (
                    <Badge variant="outline" className="border-yellow-500 text-yellow-400">
                      ⏰ Limited Time Only
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fashion Show */}
      {industryData?.fashion_show && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-6 h-6" style={{ color: theme.primary }} />
              Fashion Show & Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white text-lg mb-3">Live Fashion Show featuring latest trends</p>
              <div className="grid grid-cols-3 gap-3">
                {industryData.fashion_show.shows?.map((show: any, idx: number) => (
                  <div key={idx} className="p-3 bg-white/5 rounded text-center">
                    <p className="text-white font-bold">{show.time}</p>
                    <p className="text-xs text-slate-400">{show.theme}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gift Vouchers */}
      {industryData?.gift_vouchers && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Gift className="w-6 h-6" style={{ color: theme.primary }} />
              Gift Vouchers & Prizes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="p-4 bg-white/5 rounded-lg">
                <p className="text-white mb-2">Win gift vouchers worth:</p>
                <p className="text-4xl font-bold" style={{ color: theme.primary }}>
                  ₹{industryData.gift_vouchers.total_value?.toLocaleString()}
                </p>
              </div>
              {industryData.gift_vouchers.lucky_draw && (
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-white">🎰 Lucky Draw every hour</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product Categories */}
      {industryData?.product_categories && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white">Product Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {industryData.product_categories.map((category: string, idx: number) => (
                <Badge key={idx} style={{ backgroundColor: theme.primary }} className="text-sm py-2 px-4">
                  {category}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
